import { InputNumber, Radio } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import './PredefinedOfferingAmountRadio.less';

import styles from './PredefinedOfferingAmountRadio.module.scss';

const PredefinedOfferingAmountRadio = ({
  allowFreeOfferings,
  clearErrors,
  massOfferingAmount,
  minimalMassOfferingAmount,
  name,
  onChange,
  setError,
  optionalFreeOfferingsMessage,
  value,
  showLabel,
  displayOwnAmountFirst,
}) => {
  const [isAmountFromPredefined, setIsAmountFromPredefined] = useState(true);
  const [internalAmountValue, setInternalAmountValue] = useState(value);

  const onRadioGroupClick = event => {
    setIsAmountFromPredefined(true);
    setInternalAmountValue(event.target.value);
    onChange(parseInt(event.target.value, 10));
    clearErrors(name);
  };

  const onOwnAmountChange = newValue => {
    setIsAmountFromPredefined(false);

    const normalizedValue = typeof newValue === 'string' ? newValue.replace(',', '.') : newValue;

    const parsedNumber = parseFloat(normalizedValue);

    const hasInvalidDecimals = () => {
      const str = normalizedValue.toString();
      const decimalPart = str.includes('.') ? str.split('.')[1] : '';
      return decimalPart.length > 2;
    };

    const shouldBeHigherThanMinimalValue = parsedNumber < minimalMassOfferingAmount;
    if (Number.isNaN(parsedNumber)) {
      setError(name, {
        type: 'manual',
        message: 'Wpisz poprawną kwotę',
      });
    } else if (hasInvalidDecimals()) {
      setError(name, {
        type: 'manual',
        message: 'Kwota może mieć maksymalnie 2 miejsca po przecinku',
      });
    } else if (allowFreeOfferings && parsedNumber < 0) {
      setError(name, {
        type: 'manual',
        message: 'Kwota nie może być ujemna',
      });
    } else if ((!allowFreeOfferings && parsedNumber <= 0) || shouldBeHigherThanMinimalValue) {
      setError(name, {
        type: 'manual',
        message: `Minimalna kwota ofiary składanej za pomocą formularza online to ${
          shouldBeHigherThanMinimalValue ? minimalMassOfferingAmount : 1
        } PLN.${optionalFreeOfferingsMessage ? ` ${optionalFreeOfferingsMessage}` : ''}`,
      });
    } else {
      clearErrors(name);
      onChange(parsedNumber);
    }
    setInternalAmountValue(normalizedValue);
  };

  const predefinedAmountsRadioGroup = Array.isArray(massOfferingAmount.predefinedValues) &&
    massOfferingAmount.predefinedValues.length > 0 && (
      <Radio.Group
        value={isAmountFromPredefined ? internalAmountValue : ''}
        size="large"
        className={styles.radioGroup}
        onChange={onRadioGroupClick}
      >
        {massOfferingAmount.predefinedValues.map(offering => (
          <Radio.Button key={offering} value={offering}>
            {offering}
          </Radio.Button>
        ))}
      </Radio.Group>
    );

  const ownAmountInput = (
    <>
      {(massOfferingAmount.allowOwnAmount || allowFreeOfferings) && (
        <InputNumber
          value={isAmountFromPredefined ? '' : internalAmountValue}
          onChange={onOwnAmountChange}
          placeholder="Wpisz własną kwotę"
          required={!isAmountFromPredefined}
          parser={newValue => parseFloat(`${newValue}`.replace(',', '.'))}
        />
      )}
    </>
  );

  return (
    <React.Fragment>
      <input type="hidden" name={name} value={internalAmountValue} />

      <div
        className={classNames(
          'antd-radio-group-wrapper md-form input-group mt-0 mb-0',
          styles.self,
        )}
      >
        {showLabel && <div className={styles.label}>Ofiara w&nbsp;PLN:</div>}

        {displayOwnAmountFirst ? (
          <>
            {ownAmountInput}
            {predefinedAmountsRadioGroup}
          </>
        ) : (
          <>
            {predefinedAmountsRadioGroup}
            {ownAmountInput}
          </>
        )}
      </div>
    </React.Fragment>
  );
};

PredefinedOfferingAmountRadio.propTypes = {
  allowFreeOfferings: PropTypes.bool,
  massOfferingAmount: PropTypes.shape({
    predefinedValues: PropTypes.array,
    allowOwnAmount: PropTypes.bool,
    minimalValue: PropTypes.number, // unused - we're using minimalMassOfferingAmount
  }),
  minimalMassOfferingAmount: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  name: PropTypes.string,
  setError: PropTypes.func,
  clearErrors: PropTypes.func,
  optionalFreeOfferingsMessage: PropTypes.string,
  value: PropTypes.number,
  showLabel: PropTypes.bool,
  displayOwnAmountFirst: PropTypes.bool,
};

export default PredefinedOfferingAmountRadio;
